import {
  CustomDropdownItemOption,
  type DropdownIconComponentProps,
  DropDownItemOptionComponentProps,
  SelectDropdown,
  SelectOption,
} from '../../components/SelectDropdown/SelectDropdown'
import React, { useCallback, useMemo, useState } from 'react'
import { type MeetingTemplate, useGetDefaultMeetingTemplateQuery, useGetMeetingTemplatesQuery } from '../../store/ai/meetingTemplate'
import { useAppSelector } from '../../store/hooks'
import { selectAccountId, selectSafeAuthenticatedUser } from '../../store/auth/authSlice'
import { TagElement } from '../../components/Tag/Tag'
import { useUpdateSessionMetadataMutation } from '../../store/media/media'
import { RoomSession } from '../../models/Room'
import { parseMetadata } from '../../common/util'
import { getRoomTrackingDetails } from '../../apps/professional-app/fa/tracking'
import { TrackEvent, useTracking } from '../../apps/professional-app/fa/common/tracking'
import { Spinner } from 'react-bootstrap'
import { MeetingTemplateVisibilityChangeConfirmationModal } from './MeetingTemplateVisibilityChangeConfirmationModel'

export interface MeetingTemplateSelectorProps {
  session: RoomSession
  onSessionChange?: (roomSession: RoomSession) => void
}

export const MeetingTemplateSelector: React.FC<MeetingTemplateSelectorProps> = ({ session, onSessionChange }) => {
  const accountId = useAppSelector(selectAccountId)
  const { roomId, id: sessionId } = session
  const { id: currentUserId } = useAppSelector(selectSafeAuthenticatedUser)
  const {
    privacySource,
    external: externalSessionMetadata,
    meetingTemplateId,
    aiSuggestedTemplate,
  } = useMemo(() => {
    const parsed = parseMetadata(session.metadata)
    return {
      privacySource: parsed.privacySource,
      external: parsed.external,
      meetingTemplateId: (parsed.external?.meetingTemplateId as string) ?? null,
      aiSuggestedTemplate: (parsed.external?.aiSuggestedTemplate as string) ?? null,
    }
  }, [session.metadata])
  const { data: meetingTemplates, isFetching } = useGetMeetingTemplatesQuery(
    { accountId, pageRequest: { page: 0, size: 500 }, includeInactive: false },
    { refetchOnMountOrArgChange: true },
  )
  const { data: defaultMeetingTemplate } = useGetDefaultMeetingTemplateQuery(
    {
      accountId,
      userId: currentUserId,
    },
    { refetchOnMountOrArgChange: true },
  )
  const [updateRoomSessionMetadata, { isLoading: isUpdatingMetadata }] = useUpdateSessionMetadataMutation()
  const { tracked } = useTracking()
  const [showModal, setShowModal] = useState(false)
  const [meetingTemplateCandidate, setMeetingTemplateCandidate] = useState<MeetingTemplate | null>(null)

  const meetingTemplate = useMemo(() => {
    if (!meetingTemplates?.data) return null
    const sessionMeetingTemplate = meetingTemplateId ? meetingTemplates.data.find((template) => template.id === meetingTemplateId) : null
    return sessionMeetingTemplate ?? defaultMeetingTemplate ?? null
  }, [meetingTemplates, defaultMeetingTemplate, meetingTemplateId])

  const currentIsDeprecated = meetingTemplate?.isDeprecated === true
  const newVersionId = meetingTemplate?.newMeetingTemplateId

  const meetingTemplateOptions = useMemo(
    () =>
      meetingTemplates?.data
        .filter((template) => {
          if (!template.isDeprecated) return true
          if (currentIsDeprecated && template.id === meetingTemplate?.id) return true
          return false
        })
        .map(({ id, name }) => ({
          id,
          label: name,
        }))
        .sort((left, right) => left.label.localeCompare(right.label)) ?? [],
    [meetingTemplates, currentIsDeprecated, meetingTemplate],
  )

  const MeetingTemplateIcon: React.FC<DropdownIconComponentProps> = useCallback(
    () => (
      <span className='position-relative d-inline-flex'>
        <i className='bi bi-columns-gap' />
        {currentIsDeprecated ? <span className='deprecated-template-icon-dot' /> : null}
      </span>
    ),
    [currentIsDeprecated],
  )

  const UpcomingMeetingTemplateDropdownItemOption: React.FC<DropDownItemOptionComponentProps> = useCallback(
    ({ optionId, optionLabel, onClick }) => {
      const showAITag = aiSuggestedTemplate === optionId
      const isLegacy = currentIsDeprecated && optionId === meetingTemplate?.id
      const isNewVersion = currentIsDeprecated && optionId === newVersionId
      const isCurrent = meetingTemplate?.id === optionId

      return (
        <CustomDropdownItemOption role='button' onClick={onClick} className='d-flex zocks-gap-0.5 align-items-center'>
          <div className='d-flex flex-grow-1 align-items-center justify-content-between'>
            <span>
              {optionLabel}
              {isLegacy ? <span className='text-muted ms-1'>(Legacy)</span> : null}
            </span>
            <div className='d-flex align-items-center gap-1'>
              {isCurrent ? <span className='zocks-text-primary fw-medium paragraph-small'>Current</span> : null}
              {isNewVersion ? <TagElement className='meeting-template-new-version-tag'>New version</TagElement> : null}
              {showAITag ? (
                <TagElement className='bg-transparent zocks-text-primary zocks-border-0.25'>
                  <i className='bi bi-stars'></i>
                </TagElement>
              ) : null}
            </div>
          </div>
        </CustomDropdownItemOption>
      )
    },
    [aiSuggestedTemplate, currentIsDeprecated, meetingTemplate?.id, newVersionId],
  )

  const _applyMeetingTemplateSelected = (selectedMeetingTemplate: SelectOption) => {
    const { id } = selectedMeetingTemplate
    updateRoomSessionMetadata({
      accountId,
      roomId,
      sessionId,
      metadata: {
        ...externalSessionMetadata,
        meetingTemplateId: id,
      },
    }).then(() => {
      const newMeetingTemplate = meetingTemplates?.data.find((template) => template.id === id) ?? null
      if (!['manual', 'calendar'].includes(privacySource)) {
        onSessionChange?.({
          ...session,
          isPrivate: newMeetingTemplate?.isPrivate ?? false,
          metadata: JSON.stringify({ ...parseMetadata(session.metadata), privacySource: 'meetingTemplate' }),
        })
      }
      onHide()
    })
  }

  const applyMeetingTemplateSelected = tracked(
    _applyMeetingTemplateSelected,
    { eventName: TrackEvent.ROOM_SESSION_MEETING_TYPE_CHANGED, eventDetails: getRoomTrackingDetails(session) },
    {
      eventDetailsExtractor: ({ id: selectedMeetingTemplateId }: SelectOption) => {
        return {
          selectedMeetingTemplateId,
          source: 'upcoming',
        }
      },
    },
  )

  const handleMeetingTemplateSelected = (selectedMeetingTemplate: SelectOption) => {
    const template = meetingTemplates?.data.find((candidate) => candidate.id === selectedMeetingTemplate.id)
    if (template && template.isPrivate !== session.isPrivate && !['manual', 'calendar'].includes(privacySource)) {
      setMeetingTemplateCandidate(template)
      setShowModal(true)
    } else {
      applyMeetingTemplateSelected(selectedMeetingTemplate)
    }
  }

  const onHide = () => {
    setShowModal(false)
    setMeetingTemplateCandidate(null)
  }

  return isFetching || isUpdatingMetadata ? (
    <div className='d-flex align-items-center justify-content-center p-2'>
      <Spinner size={'sm'} />
    </div>
  ) : (
    <>
      {meetingTemplateCandidate && (
        <MeetingTemplateVisibilityChangeConfirmationModal
          meetingTemplate={meetingTemplateCandidate}
          onConfirm={() => applyMeetingTemplateSelected({ id: meetingTemplateCandidate.id, label: meetingTemplateCandidate.name })}
          show={showModal}
          onHide={onHide}
        />
      )}
      <SelectDropdown
        className='p-2 w-100'
        IconComponent={MeetingTemplateIcon}
        initialSelectedOption={
          meetingTemplate
            ? {
                id: meetingTemplate.id,
                label: meetingTemplate.name,
              }
            : null
        }
        options={meetingTemplateOptions}
        onOptionSelected={handleMeetingTemplateSelected}
        disabled={!meetingTemplates?.data.length}
        DropDownItemOptionComponent={UpcomingMeetingTemplateDropdownItemOption}
      />
    </>
  )
}
